import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Users, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { GoldButton, GhostButton } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { DocRow, useOpenDoc } from "@/components/documents/DocumentList";

const ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt";
const MAX = 20 * 1024 * 1024;

const ShareDialog = ({ doc, onClose, onSaved }) => {
  const { t } = useLang();
  const { data: staff } = useFetch("/employees", { status: "active" });
  const [mode, setMode] = useState(doc.share_mode);
  const [ids, setIds] = useState(doc.shared_with || []);
  const [q, setQ] = useState("");
  const toggle = (id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    try {
      await api.put(`/documents/${doc.id}`, { share_mode: mode, shared_with: ids });
      toast.success(t("c.saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  const list = (staff || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="doc-share-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("doc.share")}</DialogTitle><p className="text-sm text-neutral-500 truncate">{doc.filename}</p></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-2">
          {["assigned", "custom"].map((k) => (
            <button key={k} data-testid={`doc-share-mode-${k}`} aria-pressed={mode === k} onClick={() => setMode(k)}
              className={`press text-left border rounded-xl px-4 py-3 ${mode === k ? "border-[#C5A059] bg-[#C5A059]/10" : "border-black/10 hover:border-black/30"}`}>
              <div className="text-sm font-medium">{t(`doc.mode.${k}`)}</div><div className="text-[11px] text-neutral-500 mt-0.5">{t(`doc.mode.${k}Hint`)}</div>
            </button>
          ))}
        </div>
        <div>
          <div className="eyebrow mb-2">{mode === "assigned" ? t("doc.alsoShare") : t("doc.pickPeople")} ({ids.length})</div>
          <Input data-testid="doc-share-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("c.search")} className="h-9 rounded-none mb-2" />
          <div className="max-h-56 overflow-y-auto divide-y divide-black/[0.05] border border-black/[0.06] rounded-xl px-3">
            {list.map((s) => (
              <label key={s.id} className="flex items-center gap-3 py-2 text-sm cursor-pointer">
                <Checkbox data-testid={`doc-share-person-${s.id}`} checked={ids.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                <span className="truncate">{s.name}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2"><GhostButton onClick={onClose}>{t("c.cancel")}</GhostButton><GoldButton data-testid="doc-share-save" onClick={save}>{t("c.save")}</GoldButton></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EventDocumentsAdmin = ({ eventId }) => {
  const { t } = useLang();
  const { data, reload } = useFetch(`/events/${eventId}/documents`);
  const { open, busy } = useOpenDoc();
  const [uploading, setUploading] = useState(false);
  const [sharing, setSharing] = useState(null);
  const input = useRef(null);
  const upload = async (files) => {
    const list = [...files];
    if (list.some((f) => f.size > MAX)) return toast.error(t("err.file_too_large"));
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/events/${eventId}/documents`, fd);
      }
      toast.success(t("doc.uploaded"));
      reload();
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setUploading(false);
      input.current.value = "";
    }
  };
  const remove = async (d) => {
    if (!window.confirm(t("doc.confirmDelete"))) return;
    try {
      await api.delete(`/documents/${d.id}`);
      reload();
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  return (
    <div className="card-lux p-6 min-w-0" data-testid="event-documents-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div><h3 className="font-display text-2xl">{t("doc.title")}</h3><p className="text-xs text-neutral-500">{t("doc.sub")}</p></div>
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" data-testid="doc-file-input" onChange={(e) => e.target.files.length && upload(e.target.files)} />
        <GoldButton data-testid="doc-upload-btn" disabled={uploading} onClick={() => input.current.click()}>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{t("doc.upload")}</GoldButton>
      </div>
      {data && data.length === 0 && <p className="text-sm text-neutral-500 py-3" data-testid="event-documents-empty">{t("doc.none")}</p>}
      <ul className="divide-y divide-black/[0.05]">
        {(data || []).map((d) => (
          <DocRow key={d.id} d={d} onOpen={open} busy={busy}>
            <button data-testid={`doc-share-${d.id}`} onClick={() => setSharing(d)} className="press hidden sm:inline-flex items-center gap-1.5 text-[11px] border border-black/10 rounded-full px-3 py-1.5 hover:border-[#C5A059]">
              <Users className="w-3.5 h-3.5" />{d.share_mode === "assigned" ? t("doc.mode.assigned") : t("doc.mode.custom")}{d.shared_with?.length ? ` +${d.shared_with.length}` : ""}
            </button>
            <button data-testid={`doc-share-m-${d.id}`} onClick={() => setSharing(d)} className="press sm:hidden p-2 rounded-full border border-black/10" aria-label={t("doc.share")}><Users className="w-3.5 h-3.5" /></button>
            <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)} className="press p-2 rounded-full border border-red-400/40 text-red-500 hover:bg-red-500/10" aria-label={t("c.delete")}><Trash2 className="w-3.5 h-3.5" /></button>
          </DocRow>
        ))}
      </ul>
      {sharing && <ShareDialog doc={sharing} onClose={() => setSharing(null)} onSaved={reload} />}
    </div>
  );
};
